import { useCallback, useMemo } from 'react';
import { useAppInsights } from './useAppInsights';
import { TripsPanelState, type TripsPanelStateManager, Event, type Page } from '../types';
import { useDispatch, useTripsContext } from '../components/GlobalContextProvider/GlobalContextProvider';
import { ActionType } from '../components/GlobalContextProvider/global-reducer';
import { getEventFactory } from '../utils/get-event-factory';

export const useTripsPanelStateManager = (
  page: Page,
  isMobileView: boolean,
  operationName: string
): TripsPanelStateManager => {
  const dispatch = useDispatch();
  const appInsights = useAppInsights();
  const { tripsPanelState } = useTripsContext();

  const updateTripsPanelState = (state: TripsPanelState): void => {
    dispatch({
      type: ActionType.SET_TRIPS_PANEL_STATE,
      payload: {
        tripsPanelState: state,
      },
    });
  };

  const closeTripsPanel = useCallback(() => {
    updateTripsPanelState(TripsPanelState.Closed);
  }, [isMobileView]);

  const toggleTripsPanel = useCallback(() => {
    updateTripsPanelState(tripsPanelState === TripsPanelState.Open ? TripsPanelState.Closed : TripsPanelState.Open);
  }, [isMobileView, tripsPanelState]);

  const handleTripsPanelClose = useCallback((): void => {
    const event = getEventFactory(Event.TripsHistoryPanelClosed)(page, operationName);
    appInsights.trackAnalyticsEvent(event);
    closeTripsPanel();
  }, [page, operationName]);

  const handleTripsPanelToggle = useCallback((): void => {
    const eventName =
      tripsPanelState === TripsPanelState.Open || tripsPanelState === TripsPanelState.Reopen
        ? Event.TripsHistoryPanelOpened
        : Event.TripsHistoryPanelClosed;

    const event = getEventFactory(eventName)(page, operationName);
    appInsights.trackAnalyticsEvent(event);
    toggleTripsPanel();
  }, [tripsPanelState, page, operationName]);

  const handleTripsPanelOpen = useCallback((): void => {
    updateTripsPanelState(TripsPanelState.Open);
  }, []);

  const handleCloseToReopen = useCallback(() => {
    updateTripsPanelState(TripsPanelState.Reopen);
  }, []);

  return useMemo(() => {
    return {
      close: handleTripsPanelClose,
      open: handleTripsPanelOpen,
      toggle: handleTripsPanelToggle,
      closeToReopen: handleCloseToReopen,
    };
  }, [handleTripsPanelClose, handleTripsPanelOpen, handleTripsPanelToggle, handleCloseToReopen]);
};
